import React from 'react';
import { OnEnterArgs, State } from '..';
import { addNotification } from 'utils/NotificationManager';
import { KeysOfType } from 'component_utils/typescriptUtils';
import Translation from 'utils/Language/Translation';
import { ScannerInputModes } from 'components/Scanner';
import { IBatch, IConfigDto, IItem } from 'interfaces';
import { matchBatch, matchItem } from 'component_utils/objectUtils';
import { isFunction, isNil } from 'lodash';
import { MaybePromise } from 'component_utils/utils';
import VictorySfx from 'audio/victory1.mp3';
import { CompoundBarcode, compoundItemBarcodeParser } from 'utils/Config';
import { hasBatch, isBatch, isNormal, isSerial } from 'component_utils/itemLocationTree';

export type QuantityUpdate = { 
  qty: number; 
  additive: boolean; 
  finish: boolean; 
  type: 'QUANTITY' | 'QUANTITY_DELTA' | 'ITEM' | 'CONFIRM'; 
  compoundBarcode?: CompoundBarcode; 
}

const unitParser = /^(?<number>\d*\.?\d*)\s*?(?<unit>[^\d\s]+)$/

export function getMultiplierForBarcode(item: IItem, _barcode: string) {
  const barcode = _barcode.toLowerCase().trim()

  if (item.barcode.toLowerCase().trim() === barcode) {
    return item.barcodeInvQty
  }

  if (item.barcode2.toLowerCase().trim() === barcode) {
    return item.barcode2InvQty
  }

  if (item.barcode3.toLowerCase().trim() === barcode) {
    return item.barcode3InvQty
  }

  return 1.0
}

export async function getQtyFromString(
  s: string,
  config: IConfigDto,
  item?: IItem,
  batch?: IBatch,
  lot?: string,
  desiredUnit?: string,
): Promise<QuantityUpdate> {
  if (item) {
    if (s !== '0') {
      const compound = await compoundItemBarcodeParser(s, () => [item], 'itemCode')
      const { itemCode, batchCode } = compound
      const allowBatchSerialItemScanWithoutBatchCheck = config.settings.scannerQtyAllowBatchSerialItemScanWithoutBatchCheck
      const multiplier = getMultiplierForBarcode(item, itemCode)
      if (matchItem(item, itemCode)) {
        if (isNormal(item)) {
          return { qty: multiplier, additive: true, finish: false, type: 'ITEM', compoundBarcode: compound };
        } else if (isBatch(item) && (isNil(batch) || (allowBatchSerialItemScanWithoutBatchCheck && isNil(batchCode)) || matchBatch(batch, batchCode))) {
          return { qty: multiplier, additive: true, finish: false, type: 'ITEM', compoundBarcode: compound };
        } else if (isSerial(item) && (isNil(lot) || (allowBatchSerialItemScanWithoutBatchCheck && isNil(batchCode)) || lot === batchCode)) {
          return { qty: multiplier, additive: true, finish: false, type: 'ITEM', compoundBarcode: compound };
        } else if (hasBatch(item) && !allowBatchSerialItemScanWithoutBatchCheck && !isNil(batch) && isNil(batchCode)) {
          throw addNotification('danger', <Translation name="T.errors.batchCodeNotPresentForValidation"/>)
        } else {
          throw addNotification('danger', <Translation name="T.errors.wrongItemScanned"/>)
        }
      }
    }
  }

  let matched = null;
  matched = s.match(config.settings.scannerPlus);
  if (matched) {
    return { qty: parseFloat(matched[1]), additive: true, finish: false, type: 'QUANTITY_DELTA' };
  }

  matched = s.match(config.settings.scannerMinus);
  if (matched) {
    return { qty: parseFloat(matched[1]) * -1, additive: true, finish: false, type: 'QUANTITY_DELTA' };
  }

  if (s === '' || s.match(config.settings.scannerOk)) {
    return { qty: 0, additive: true, finish: true, type: 'CONFIRM' };
  }

  // attempt to match a number + unit
  matched = s.match(unitParser)
  if (matched && matched[1] && matched[2]) {
    const qty = parseFloat(matched[1])
    if (isNaN(qty) || isNaN(Number(matched[1]))) {
      throw addNotification('danger', <Translation name="T.errors.notANumber" />);
    }

    if (!desiredUnit) {
      return { qty, additive: false, finish: true, type: 'QUANTITY' };
    }

    // convert the unit to the desired unit type
    const unit = matched[2].toLowerCase().trim()
    const targetUnit = desiredUnit.toLowerCase().trim()

    // no conversion needed if unit and target are the same
    if (unit === targetUnit) {
      return { qty, additive: false, finish: true, type: 'QUANTITY' };
    }

    const unitTable = config.settings.weightTable.map(it => ({
      type: 'weight',
      name: it.name,
      toMG: it.toMG,
      toML: !isNil(item?.mlToMg) ? it.toMG * (1.0 / item.mlToMg) : null
    })).concat(config.settings.volumeTable.map(it => ({
      type: 'volume',
      name: it.name,
      toMG: !isNil(item?.mlToMg) ? it.toML * item?.mlToMg : null,
      toML: it.toML
    })))

    const unitEntry = unitTable.find(it => it.name.toLowerCase().trim() === unit)
    const targetUnitEntry = unitTable.find(it => it.name.toLowerCase().trim() === targetUnit)
    if (!unitEntry || !targetUnitEntry) {
      throw addNotification('danger', <Translation name="T.errors.couldNotMatchUnits" params={{ unit, targetUnit }} />);
    }

    let conversionField: 'toMG' | 'toML' = null
    if (targetUnitEntry.type === 'weight') {
      conversionField = 'toMG'
    } else if (targetUnitEntry.type === 'volume') {
      conversionField = 'toML'
    }

    if (!conversionField || isNil(unitEntry[conversionField])) {
      throw addNotification('danger', <Translation name="T.errors.couldNotMatchUnits" params={{ unit, targetUnit }} />);
    }

    const convertedQty = qty * unitEntry[conversionField] * (1.0 / targetUnitEntry[conversionField])
    return { qty: convertedQty, additive: false, finish: true, type: 'QUANTITY' };
  }

  // single qty field
  const qty = parseFloat(s);
  if (isNaN(qty) || isNaN(Number(s))) {
    throw addNotification('danger', <Translation name="T.errors.notANumber" />);
  } else if (qty > config.settings.scannerQtyMaxAtOnce) {
    throw addNotification('danger', <Translation name="T.errors.qtyTooLarge" />);
  } else {
    return { qty, additive: false, finish: true, type: 'QUANTITY' };
  }
}

function getMetaKeys<S>(quantityKey: KeysOfType<S, number>) {
  return {
    firstCycle: `state-qty-${quantityKey}-first-cycle`,
  };
}

interface EventProps {
  config: IConfigDto;
}

interface Config<S, P, A, R> {
  nextState: number;
  repeatState?: number;
  quantityKey: KeysOfType<S, number>;
  quantityUpdateKey?: KeysOfType<S, QuantityUpdate>;

  itemKey?: KeysOfType<S, IItem>;
  batchKey?: KeysOfType<S, IBatch>;
  lotKey?: KeysOfType<S, string>;

  getItemIdentificationStepScannedCode?: (args: OnEnterArgs<S, P, R>) => MaybePromise<string>;
  getDesiredUnit?: (args: OnEnterArgs<S, P, R>) => MaybePromise<string>;

  preSetQuantity?: (args: OnEnterArgs<S, P, R>, qty: number) => MaybePromise<any>;

  attributes?: Partial<A>;
  startAtOne?: boolean | ((args: OnEnterArgs<S, P, R>) => MaybePromise<boolean>);
  getTargetQuantity?: (args: OnEnterArgs<S, P, R>) => MaybePromise<number>;
}

const StateQuantity = function <S, P extends EventProps, A extends { hint: string; inputType: ScannerInputModes }, R>({
  nextState,
  repeatState,

  quantityKey,
  quantityUpdateKey,
  itemKey,
  batchKey,
  lotKey,
  
  getItemIdentificationStepScannedCode,
  getDesiredUnit,

  preSetQuantity,

  attributes = {},
  startAtOne,
  getTargetQuantity,
}: Config<S, P, A, R>): State<S, P, A, R> {
  const { firstCycle } = getMetaKeys<S>(quantityKey);
  return {
    onEnter: async (args) => {
      const { state } = args;
      const isFirstCycle = (state as any)[firstCycle] === undefined;
      if (isFirstCycle && !isNil(state[quantityKey])) {
        return {
          type: 'MOVE',
          nextState: nextState,
        };
      }
      
      let startAtOneEnabled = isFunction(startAtOne) ? (await startAtOne?.(args)) : startAtOne
      let startAtOneQty = 1.0
      if (startAtOneEnabled && itemKey && getItemIdentificationStepScannedCode) {
        startAtOneQty = getMultiplierForBarcode(
          state[itemKey] as unknown as IItem,
          await getItemIdentificationStepScannedCode(args)
        )
      }

      let currentQty = isFirstCycle ? (startAtOneEnabled ? startAtOneQty : 0) : (state[quantityKey] as unknown as number);
      let satisfiedQty = false;
      if (getTargetQuantity) {
        const targetQty = await getTargetQuantity(args);
        satisfiedQty = currentQty === targetQty;
      }

      // we scanned enough continue
      if (satisfiedQty) {
        preSetQuantity?.(args, currentQty)
        new Audio(VictorySfx).play();
        return {
          type: 'MOVE',
          nextState: nextState,
          data: {
            [firstCycle]: undefined,
            [quantityKey]: currentQty,
          } as any,
        };
      }

      if (startAtOneEnabled && isFirstCycle) {
        preSetQuantity?.(args, currentQty)
        return {
          type: 'MOVE',
          nextState: isNil(repeatState) ? args.currentState : repeatState,
          data: {
            [firstCycle]: isFirstCycle,
            [quantityKey]: currentQty,
          } as any,
        };
      }

      preSetQuantity?.(args, currentQty)
      return {
        type: 'RESULT',
        data: {
          [firstCycle]: isFirstCycle,
          [quantityKey]: currentQty,
        } as any,
      };
    },
    onAction: async (args) => {
      const {
        currentState,
        state,
        props: { config },
        inputs: [s],
      } = args;

      const quantityUpdate = await getQtyFromString(
        s,
        config,
        itemKey ? (state[itemKey] as unknown as IItem) : null,
        batchKey ? (state[batchKey] as unknown as IBatch) : null,
        lotKey ? (state[lotKey] as unknown as string) : null,
        getDesiredUnit ? (await getDesiredUnit(args)) : null,
      );

      const { qty, additive, finish } = quantityUpdate
      const currentQty = state[quantityKey] as unknown as number;
      const nextQty = additive ? currentQty + qty : qty;

      preSetQuantity?.(args, nextQty)
      return {
        type: 'MOVE',
        data: {
          [quantityKey]: nextQty,
          [firstCycle]: finish ? undefined : false,
          [quantityUpdateKey]: quantityUpdate
        } as any,
        nextState: finish ? nextState : isNil(repeatState) ? currentState : repeatState,
      };
    },
    attributes: {
      hint: 'T.hints.quantity',
      inputType: 'numeric' as ScannerInputModes,
      ...(attributes as any),
    },

    nodeName: 'T.states.quantity',
    outputs: [
      {
        name: 'onScan',
        to: nextState,
      },
      {
        name: 'repeat',
        to: isNil(repeatState) ? -1 : repeatState,
      },
    ],
  };
};

// to return to the original quantity in case of repeatstate
interface RepeatConfig<S, P, A, R> {
  nextState: number;
  quantityState: number;
  quantityKey: KeysOfType<S, number>;
  attributes?: Partial<A>;
}

export const StateQuantityRepeat = function <S, P extends EventProps, A extends {}, R>({
  nextState,
  quantityState,
  quantityKey,
  attributes = {},
}: RepeatConfig<S, P, A, R>): State<S, P, A, R> {
  const { firstCycle } = getMetaKeys<S>(quantityKey);
  return {
    onEnter: async ({ state }) => {
      return {
        type: 'MOVE',
        nextState: (state as any)[firstCycle] === undefined ? nextState : quantityState,
      };
    },

    attributes: attributes as any,

    nodeName: 'T.states.quantityRepeat',
    outputs: [
      {
        name: 'onProgress',
        to: nextState,
      },
      {
        name: 'onRepeat',
        to: quantityState,
      },
    ],
  };
};

export default StateQuantity;
